/**
 * Undo and redo over an autosaved draft (Dax, 2026-10-04). Every route an edit can take (a knob, a drag,
 * the sparkle, Replace, a picker) ends in the draft the page shows, so the history records that rather than
 * each edit: once the draft has been still for UNDO_SETTLE_MS with nothing saving, its state is a step. A
 * burst of edits lands as one step. Undo hands the state to restore to the page; whatever the page then
 * sets off by itself within UNDO_ADOPT_MS (levelling, frame quantising) folds into that step rather than
 * becoming a new one, so redo survives it.
 */
import { useEffect, useRef, useState } from 'react';
import { EMPTY_HISTORY, record, redo, undo, type History } from '@/lib/draft-history';

/** How many steps back a draft keeps: snapshots are small, so plenty. */
export const UNDO_LEVELS = 50;
/** How long a draft must sit still before its state counts as a step. */
const UNDO_SETTLE_MS = 700;
/** How long after an undo or redo the page's own follow-ups belong to it. */
const UNDO_ADOPT_MS = 1500;

export function useDraftHistory<T extends { id: string }>({ draft, snapshot, busy, restore }: {
  draft: T | undefined;
  /** The draft's editable state as one string, so two states compare by value. */
  snapshot: (draft: T) => string;
  /** Whether an edit is still saving: a step is recorded only once it has landed. */
  busy: () => boolean;
  /** Put a recorded state back. */
  restore: (snap: string) => void;
}) {
  const [history, setHistory] = useState<History>(EMPTY_HISTORY);
  const forDraft = useRef<string | null>(null);
  const adoptUntil = useRef(0);
  useEffect(() => {
    if (!draft) return;
    if (forDraft.current !== draft.id) {
      forDraft.current = draft.id;
      setHistory(record(EMPTY_HISTORY, snapshot(draft), UNDO_LEVELS));
      return;
    }
    // Still saving: the refetch when it lands changes the draft and brings this back round.
    const t = setTimeout(() => {
      if (!busy()) setHistory((h) => record(h, snapshot(draft), UNDO_LEVELS, Date.now() < adoptUntil.current));
    }, UNDO_SETTLE_MS);
    return () => clearTimeout(t);
  }, [draft]);
  const step = (move: typeof undo) => {
    const r = move(history);
    if (!r) return;
    setHistory(r.history);
    adoptUntil.current = Date.now() + UNDO_ADOPT_MS;
    restore(r.restore);
  };
  return {
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    undo: () => step(undo),
    redo: () => step(redo),
  };
}
